import { compareReports, MAX_REPORT_BYTES, parseReport, verifyReportHash, type ComparableReport, type SignalDiff } from '../compare';
import { SHORT_HASH_LENGTH } from '../entropy/hash';
import { clear, h, nextId } from './dom';
import type { ExportReport } from './export';

const PREVIEW_CHARS = 400;
const preview = (s: string | null) => (s === null ? '(not in this report)' : s.length > PREVIEW_CHARS ? `${s.slice(0, PREVIEW_CHARS)}…` : s);

interface Slot {
  name: 'A' | 'B';
  report: ComparableReport | null;
  textarea: HTMLTextAreaElement;
  status: HTMLElement;
  useCurrent: HTMLButtonElement;
  element: HTMLElement;
  load(text: string, source: string): Promise<void>;
}

function describe(r: ComparableReport): string {
  const when = r.generatedAt ? new Date(r.generatedAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : 'unknown date';
  return `Loaded: ${r.browser || 'unknown browser'} · ${when} · ${r.signals.size} signals`;
}

function makeSlot(name: 'A' | 'B'): Slot {
  const fileId = nextId('compare-file');
  const textId = nextId('compare-text');
  const status = h('p', { class: 'slot-status muted', role: 'status' }, 'Nothing loaded.');
  const file = h('input', { type: 'file', id: fileId, accept: 'application/json,.json' });
  const textarea = h('textarea', { id: textId, rows: 3, spellcheck: 'false', placeholder: '…or paste the report JSON here' });
  const useCurrent = h('button', { type: 'button', class: 'button small', disabled: true }, 'Use this browser’s result');
  const slot: Slot = {
    name,
    report: null,
    textarea,
    status,
    useCurrent,
    async load(text, source) {
      const parsed = parseReport(text);
      slot.report = parsed.ok ? parsed.report : null;
      if (parsed.ok) await verifyReportHash(parsed.report);
      const suspicious = parsed.ok && (parsed.report.hashVerified === false || !parsed.report.consistent);
      status.textContent = parsed.ok ? `${describe(parsed.report)} (${source})${suspicious ? '. Warning: this file looks edited.' : ''}` : parsed.error;
      status.classList.toggle('error', !parsed.ok || suspicious);
    },
    element: h(
      'fieldset',
      { class: 'report-slot' },
      h('legend', null, `Report ${name}`),
      h('label', { for: fileId, class: 'slot-label' }, 'Report file'),
      file,
      h('label', { for: textId, class: 'slot-label' }, 'Or paste JSON'),
      textarea,
      h('div', { class: 'actions' }, useCurrent),
      status,
    ),
  };

  file.addEventListener('change', async () => {
    const f = file.files?.[0];
    if (!f) return;
    if (f.size > MAX_REPORT_BYTES) {
      slot.report = null;
      status.textContent = 'That file is too large to be a report.';
      return;
    }
    textarea.value = '';
    await slot.load(await f.text(), f.name);
  });
  textarea.addEventListener('change', () => {
    if (textarea.value.trim()) void slot.load(textarea.value, 'pasted');
  });
  return slot;
}

function diffList(diffs: SignalDiff[]): HTMLElement {
  return h(
    'ul',
    { class: 'diffs' },
    diffs.map((d) =>
      h(
        'li',
        { class: 'diff' },
        h(
          'p',
          { class: 'diff-label' },
          h('strong', null, d.label),
          h('span', { class: 'muted' }, ` · ${d.category}`),
          d.randomized ? h('span', { class: 'tag' }, 'Randomized') : null,
        ),
        h(
          'div',
          { class: 'diff-values' },
          h('div', null, h('span', { class: 'diff-side' }, 'A'), h('code', { class: 'value' }, preview(d.a))),
          h('div', null, h('span', { class: 'diff-side' }, 'B'), h('code', { class: 'value' }, preview(d.b))),
        ),
      ),
    ),
  );
}

function renderComparison(a: ComparableReport, b: ComparableReport): HTMLElement {
  const c = compareReports(a, b);
  const real = c.hashDiffs.filter((d) => !d.randomized).length;
  const verdict = !c.trustworthy
    ? 'At least one report failed its integrity check (see above), so the hash comparison cannot be trusted.'
    : c.sameHash
    ? 'Same fingerprint hash. A tracker would treat these as the same browser.'
    : c.hashDiffs.length
      ? `Different fingerprint hashes: ${c.hashDiffs.length} signal${c.hashDiffs.length === 1 ? '' : 's'} in the hash differ${real < c.hashDiffs.length ? ` (${c.hashDiffs.length - real} of them randomized)` : ''}.`
      : 'Different fingerprint hashes, but no hashed signal differs. The reports probably come from different app versions.';
  const row = (label: string, va: string, vb: string) => h('tr', null, h('th', { scope: 'row' }, label), h('td', null, va), h('td', null, vb));
  const bits = (r: ComparableReport) => `${r.band ? r.band.charAt(0).toUpperCase() + r.band.slice(1) : '?'}${r.bits !== null ? ` · ${r.bits.toFixed(1)} bits` : ''}`;
  const when = (iso: string) => {
    const d = new Date(iso);
    return iso && !Number.isNaN(d.getTime()) ? d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '?';
  };

  return h(
    'div',
    { class: 'comparison' },
    c.warnings.length ? h('ul', { class: 'compare-warnings' }, c.warnings.map((w) => h('li', null, w))) : null,
    h('p', { class: 'compare-verdict', 'data-same': String(c.sameHash && c.trustworthy) }, verdict),
    h(
      'div',
      { class: 'table-wrap' },
      h(
        'table',
        null,
        h('thead', null, h('tr', null, h('td', null), h('th', { scope: 'col' }, 'Report A'), h('th', { scope: 'col' }, 'Report B'))),
        h(
          'tbody',
          null,
          row('Browser', a.browser || '?', b.browser || '?'),
          row('Generated', when(a.generatedAt), when(b.generatedAt)),
          row('Exposure', bits(a), bits(b)),
          row('Hash', a.hash.slice(0, SHORT_HASH_LENGTH) || '?', b.hash.slice(0, SHORT_HASH_LENGTH) || '?'),
        ),
      ),
    ),
    h('h3', null, `Differences that change the fingerprint hash (${c.hashDiffs.length})`),
    c.hashDiffs.length ? diffList(c.hashDiffs) : h('p', { class: 'muted' }, 'None.'),
    h(
      'details',
      { class: 'other-diffs' },
      h('summary', null, `Other differences, not part of the hash (${c.otherDiffs.length})`),
      c.otherDiffs.length ? diffList(c.otherDiffs) : h('p', { class: 'muted' }, 'None.'),
    ),
    h('p', { class: 'muted' }, `${c.identical} signal${c.identical === 1 ? ' is' : 's are'} identical in both reports.`),
  );
}

export interface CompareView {
  /** Make "Use this browser's result" available with the latest run's report. */
  setCurrent(report: ExportReport): void;
}

export function initCompare(container: HTMLElement): CompareView {
  const slots = [makeSlot('A'), makeSlot('B')];
  const results = h('div', { class: 'compare-results', 'aria-live': 'polite' });
  const compareButton = h('button', { type: 'button', class: 'button primary' }, 'Compare');
  let current: string | null = null;

  for (const slot of slots) {
    slot.useCurrent.addEventListener('click', () => {
      if (!current) return;
      slot.textarea.value = '';
      void slot.load(current, 'this browser, current run');
    });
  }

  compareButton.addEventListener('click', async () => {
    clear(results);
    for (const slot of slots) if (slot.textarea.value.trim()) await slot.load(slot.textarea.value, 'pasted');
    const [a, b] = slots.map((s) => s.report);
    if (!a || !b) {
      results.append(h('p', { class: 'slot-status error' }, 'Load a report into both A and B first.'));
      return;
    }
    results.append(renderComparison(a, b));
  });

  clear(container);
  container.append(h('div', { class: 'report-slots' }, slots.map((s) => s.element)), h('div', { class: 'actions' }, compareButton), results);

  return {
    setCurrent(report) {
      current = JSON.stringify(report);
      for (const slot of slots) slot.useCurrent.disabled = false;
    },
  };
}
